import AsyncDropdown from "components/common/forms/AsyncDropdown";
import { Layout, LoadingWrapper, PageTitle } from "components/common/layouts";
import { EXCLUDED_REVIEWER } from "constants/config.constant";
import {
  CONSOLIDATED_360_PREVIEW_TAKE,
  CONSOLIDATED_PEER_PREVIEW_TAKE,
  CONSOLIDATED_PULSE_PREVIEW_TAKE,
} from "constants/pagination.constant";
import { FormType } from "enums/form";
import { UserStatusType } from "enums/user";
import { useError, useHttpQueryService } from "hooks";
import { useCallback, useMemo, useState } from "react";
import { SingleValue } from "react-select";
import { UserService } from "services";
import { IMultipleChoiceOption } from "types/input.type";
import { User } from "types/user";
import { ConsolidatedDrawerProvider } from "./ConsolidatedDrawerContext";
import FeedbackConsolidatedSection from "./FeedbackConsolidatedSection";
import FeedbackReviewSheet from "./FeedbackReviewSheet";
import OneOnOneNotesSection from "./OneOnOneNotesSection";
import PulseSurveySheet from "./PulseSurveySheet";

const ConsolidatedView: React.FC = () => {
  const [selectedMemberOption, setSelectedMemberOption] =
    useState<IMultipleChoiceOption | null>(null);

  const {
    result: members,
    isLoading: isMembersLoading,
    error: membersError,
  } = useHttpQueryService({
    request: () => UserService.getActiveMembers(),
  });

  const searchMembers = useCallback(async (searchText: string) => {
    const users = await UserService.search(searchText, UserStatusType.ACTIVE);

    return users
      .filter((user) => !EXCLUDED_REVIEWER.includes(user.email))
      .map((user) => ({
        value: user.id,
        label: `${user.fullName} (${user.email})`,
      }));
  }, []);

  const selectedMember = useMemo<User | undefined>(() => {
    if (!selectedMemberOption || !members) {
      return undefined;
    }

    return members.find(
      (member) => member.id === Number(selectedMemberOption.value),
    );
  }, [members, selectedMemberOption]);

  useError(membersError, true);

  return (
    <Layout>
      <LoadingWrapper isLoading={isMembersLoading}>
        <PageTitle className="mb-6">Consolidated View</PageTitle>
        <AsyncDropdown<false>
          id="consolidated-view-member"
          value={selectedMemberOption}
          onChange={(value: SingleValue<IMultipleChoiceOption>) =>
            setSelectedMemberOption(value)
          }
          label="Name"
          className="mb-6 w-full max-w-[472px]"
          placeholder="Find people by email..."
          request={searchMembers}
        />
        {selectedMember && (
          <>
            <ConsolidatedDrawerProvider>
              <OneOnOneNotesSection reviewee={selectedMember} />
            </ConsolidatedDrawerProvider>
            <ConsolidatedDrawerProvider>
              <FeedbackConsolidatedSection
                reviewee={selectedMember}
                formType={FormType.THREE_HUNDRED_SIXTY}
                title="360 Feedback"
                previewTake={CONSOLIDATED_360_PREVIEW_TAKE}
                freeTextOnly
                renderSheet={(sheetProps) => (
                  <FeedbackReviewSheet
                    formType={FormType.THREE_HUNDRED_SIXTY}
                    {...sheetProps}
                  />
                )}
              />
            </ConsolidatedDrawerProvider>
            <ConsolidatedDrawerProvider>
              <FeedbackConsolidatedSection
                reviewee={selectedMember}
                formType={FormType.PEER}
                title="Peer Feedback"
                previewTake={CONSOLIDATED_PEER_PREVIEW_TAKE}
                freeTextOnly
                renderSheet={(sheetProps) => (
                  <FeedbackReviewSheet
                    formType={FormType.PEER}
                    {...sheetProps}
                  />
                )}
              />
            </ConsolidatedDrawerProvider>
            <ConsolidatedDrawerProvider>
              <FeedbackConsolidatedSection
                reviewee={selectedMember}
                formType={FormType.PULSE_SURVEY}
                title="Pulse Survey"
                previewTake={CONSOLIDATED_PULSE_PREVIEW_TAKE}
                showReviewer={false}
                renderSheet={(sheetProps) => (
                  <PulseSurveySheet {...sheetProps} />
                )}
              />
            </ConsolidatedDrawerProvider>
          </>
        )}
      </LoadingWrapper>
    </Layout>
  );
};

export default ConsolidatedView;
